import { useState, useEffect } from 'react';
import { Card, CardBody, Input, Select } from '@components/ui';
import type { InvoiceWizardData } from '../../types/invoiceWizard.types';

interface ReferenceSectionProps {
  wizardData: InvoiceWizardData;
  onUpdate: (data: Partial<InvoiceWizardData>) => void;
}

export function ReferenceSection({ wizardData, onUpdate }: ReferenceSectionProps) {
  const [referenceType, setReferenceType] = useState('03'); // Por defecto: Por CUFE
  const [cufeValue, setCufeValue] = useState(wizardData.gDFRef?.dCufeRef || '');
  const [invoiceNumberPaper, setInvoiceNumberPaper] = useState(wizardData.gDFRef?.dNroFacPap || '');
  const [invoiceNumberFiscal, setInvoiceNumberFiscal] = useState(wizardData.gDFRef?.dNroFacIE || '');
  const [referenceDate, setReferenceDate] = useState(() => {
    const toLocalDatetime = (d: Date) => {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };
    return wizardData.gDFRef?.dFechaDFRef
      ? toLocalDatetime(new Date(wizardData.gDFRef.dFechaDFRef))
      : toLocalDatetime(new Date());
  });
  const [dateError, setDateError] = useState('');

  // Actualizar los datos del wizard cuando cambien los valores
  useEffect(() => {
    // Preparar el objeto de referencia según el tipo seleccionado
    const reference: any = {
      dFechaDFRef: referenceDate,
      dNroFacIE: null,
      dNroFacPap: null,
      dCufeRef: null,
    };

    switch (referenceType) {
      case '01': // Factura en papel
        reference.dNroFacPap = invoiceNumberPaper;
        break;
      case '02': // Impresora fiscal
        reference.dNroFacIE = invoiceNumberFiscal;
        break;
      case '03': // Por CUFE
        // Agregar "FE" si no lo tiene
        let cleanCufe = cufeValue.trim();
        if (cleanCufe && !cleanCufe.startsWith('FE')) {
          cleanCufe = 'FE' + cleanCufe;
        }
        reference.dCufeRef = cleanCufe;
        break;
    }

    onUpdate({ gDFRef: reference });
  }, [referenceType, cufeValue, invoiceNumberPaper, invoiceNumberFiscal, referenceDate]);

  // DGI E02-E04: Fecha del documento referenciado máximo 180 días atrás
  useEffect(() => {
    if (referenceDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const refDate = new Date(referenceDate);
      refDate.setHours(0, 0, 0, 0);
      const diffMs = today.getTime() - refDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 180) {
        setDateError('La fecha del documento referenciado no puede ser mayor a 180 días atrás (DGI E02-E04)');
      } else {
        setDateError('');
      }
    } else {
      setDateError('');
    }
  }, [referenceDate]);

  // Calcular límites de fecha (180 días atrás, 2 días adelante) - Usar hora local, no UTC
  const toLocalDatetime = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 180);
    return toLocalDatetime(date);
  };

  const getMaxDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 2);
    return toLocalDatetime(date);
  };

  return (
    <Card data-testid="invoice-wizard-reference-section-container">
      <CardBody className="p-4">
        <div className="space-y-4">
          {/* Campos de referencia */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Tipo de referencia */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Tipo de referencia
                </label>
                <Select
                  value={referenceType}
                  onChange={(e) => {
                    setReferenceType(e.target.value);
                    // Limpiar valores al cambiar tipo
                    setCufeValue('');
                    setInvoiceNumberPaper('');
                    setInvoiceNumberFiscal('');
                  }}
                  data-testid="invoice-wizard-reference-section-type-select"
                >
                  <option value="01">Referencia factura en papel</option>
                  <option value="02">Referencia factura impresora fiscal</option>
                  <option value="03">Referencia por CUFE</option>
                </Select>
              </div>

              {/* Fecha de la factura */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                  Fecha de la factura
                </label>
                <Input
                  type="datetime-local"
                  value={referenceDate}
                  onChange={(e) => setReferenceDate(e.target.value)}
                  min={getMinDate()}
                  max={getMaxDate()}
                  className={dateError ? 'border-danger-500' : ''}
                  data-testid="invoice-wizard-reference-section-date-input"
                />
                {dateError && (
                  <p className="text-xs text-danger-600 dark:text-danger-400 mt-1" data-testid="invoice-wizard-reference-section-date-error">{dateError}</p>
                )}
              </div>

              {/* Campo según tipo de referencia */}
              {referenceType === '01' && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Número de referencia factura en papel
                  </label>
                  <Input
                    type="text"
                    value={invoiceNumberPaper}
                    onChange={(e) => setInvoiceNumberPaper(e.target.value)}
                    placeholder="Escriba el número de referencia de la factura en papel"
                    maxLength={20}
                    data-testid="invoice-wizard-reference-section-paper-number-input"
                  />
                </div>
              )}

              {referenceType === '02' && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Número de referencia impresora fiscal
                  </label>
                  <Input
                    type="text"
                    value={invoiceNumberFiscal}
                    onChange={(e) => setInvoiceNumberFiscal(e.target.value)}
                    placeholder="Escriba el número de referencia de la factura en impresora fiscal"
                    maxLength={20}
                    data-testid="invoice-wizard-reference-section-fiscal-number-input"
                  />
                </div>
              )}

              {referenceType === '03' && (
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    CUFE de referencia <span className="text-danger-600">*</span>
                  </label>
                  <Input
                    type="text"
                    value={cufeValue}
                    onChange={(e) => {
                      // Solo permitir hasta 66 caracteres (sin contar el prefijo FE)
                      const value = e.target.value;
                      // Remover "FE" si lo escribe, para normalizar
                      const cleanValue = value.startsWith('FE') ? value.slice(2) : value;
                      if (cleanValue.length <= 64) {
                        setCufeValue(cleanValue);
                      }
                    }}
                    placeholder="Escriba el CUFE de la factura (sin el prefijo FE)"
                    className="font-mono"
                    maxLength={64}
                    data-testid="invoice-wizard-reference-section-cufe-input"
                  />
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Ingrese 64 caracteres. El prefijo "FE" se agregará automáticamente.
                    {cufeValue && ` Longitud actual: ${cufeValue.length}/64`}
                  </p>
                </div>
              )}
            </div>

          {/* Información sobre las referencias */}
          <div className="mt-4 p-3 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-700 rounded-lg">
            <div className="flex items-start space-x-2">
              <svg className="w-5 h-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-info-700 dark:text-info-300">
                <p className="font-medium mb-1">Información de Referencias:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>La referencia es obligatoria para notas de crédito/débito que referencian FE</li>
                  <li>El CUFE debe tener exactamente 64 caracteres (el sistema agregará "FE" automáticamente)</li>
                  <li>La fecha debe estar dentro del rango permitido (180 días atrás, 2 días adelante)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
